import { Article, Briefing } from '@interfaces/homepage';
import { api_bookmarks_path, lookup_api_bookmarks_path } from '@routes';
import { ContentType } from '@tracking';

import request from './request';

export const BOOKMARK_STATUS_KEY = 'BOOKMARK_STATUS_KEY';

// Mirrors the server-side cap (`Api::BookmarksController::MAX_LOOKUP_ITEMS`).
export const MAX_LOOKUP_ITEMS = 100;

export enum BookmarkableType {
  Article = 'Article',
  Briefing = 'Briefing',
}

export interface BookmarkContent {
  content: (Article | Briefing)[];
  page: number;
  per_page: number;
  total_pages: number;
}

export interface BookmarksContentRequest {
  page?: number;
  per_page: number;
}

export interface BookmarksContentResponse {
  bookmarks: BookmarkResponse[];
  bookmarks_content: (Article | Briefing)[];
  page: number;
  per_page: number;
  total_pages: number;
  total_count: number;
  totalCount: number;
  totalPages: number;
  current_page: number;
  next_page: number | null;
  nextPage: number | null;
  prev_page: number | null;
  has_next_page: boolean;
  hasNextPage: boolean;
  has_prev_page: boolean;
}

export interface BookmarkDestroyResponse {
  message: string;
}

const CONTENT_TYPE_TO_BOOKMARKABLE_TYPE: Partial<Record<ContentType, BookmarkableType>> = {
  [ContentType.Article]: BookmarkableType.Article,
  [ContentType.Briefing]: BookmarkableType.Briefing,
};

export interface BookmarkContentParams {
  type: BookmarkableType;
  id: number;
}

export interface BookmarkLookupRequest {
  items: BookmarkContentParams[];
}

export interface BookmarkLookupItem extends BookmarkContentParams {
  bookmarked: boolean;
  bookmarked_at: string | null;
}

export interface BookmarkLookupResponse {
  items: BookmarkLookupItem[];
}

export interface BookmarkResponse {
  id: number;
  type: BookmarkableType;
  content_id: number;
  created_at: string;
}

export function contentTypeToBookmarkableType(contentType: ContentType): BookmarkableType | null {
  return CONTENT_TYPE_TO_BOOKMARKABLE_TYPE[contentType] ?? null;
}

export function bookmarkStatusQueryKey(
  userId: number | string | null | undefined,
  bookmarkableType: BookmarkableType,
  contentId: number
) {
  return [BOOKMARK_STATUS_KEY, userId, bookmarkableType, contentId];
}

export async function lookupBookmarkStatus({
  type,
  id,
}: BookmarkContentParams): Promise<BookmarkLookupItem> {
  const response = await request('post', lookup_api_bookmarks_path(), {
    items: [{ type, id }],
  } as BookmarkLookupRequest);

  const item = response?.items?.[0] as BookmarkLookupItem | undefined;

  if (!item) {
    return {
      type,
      id,
      bookmarked: false,
      bookmarked_at: null,
    };
  }

  return item;
}

export async function lookupBookmarkStatuses(
  items: BookmarkContentParams[]
): Promise<BookmarkLookupItem[]> {
  if (items.length === 0) {
    return [];
  }

  const response = await request('post', lookup_api_bookmarks_path(), {
    items,
  } as BookmarkLookupRequest);

  return (response?.items as BookmarkLookupItem[]) ?? [];
}

function bookmarkItemKey(type: BookmarkableType, id: number): string {
  return `${type}:${id}`;
}

function unbookmarkedStatus(type: BookmarkableType, id: number): BookmarkLookupItem {
  return { type, id, bookmarked: false, bookmarked_at: null };
}

function chunkItems<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

interface PendingBookmarkLookup {
  params: BookmarkContentParams;
  resolve: (item: BookmarkLookupItem) => void;
  reject: (error: unknown) => void;
}

let pendingBookmarkLookups: PendingBookmarkLookup[] = [];
let flushScheduled = false;

async function flushBookmarkStatusQueue(): Promise<void> {
  const entries = pendingBookmarkLookups;
  pendingBookmarkLookups = [];
  flushScheduled = false;

  const uniqueParams = new Map<string, BookmarkContentParams>();
  entries.forEach(({ params }) => {
    uniqueParams.set(bookmarkItemKey(params.type, params.id), params);
  });

  try {
    const chunks = chunkItems(Array.from(uniqueParams.values()), MAX_LOOKUP_ITEMS);
    const responses = await Promise.all(chunks.map((chunk) => lookupBookmarkStatuses(chunk)));

    const resultsByKey = new Map<string, BookmarkLookupItem>();
    responses.flat().forEach((item) => {
      resultsByKey.set(bookmarkItemKey(item.type, item.id), item);
    });

    entries.forEach(({ params, resolve }) => {
      resolve(
        resultsByKey.get(bookmarkItemKey(params.type, params.id)) ??
          unbookmarkedStatus(params.type, params.id)
      );
    });
  } catch (error) {
    entries.forEach(({ reject }) => reject(error));
  }
}

// DataLoader-style batcher: coalesces every status lookup requested within the
// same tick into a single (chunked) POST to the lookup endpoint. This lets a
// whole feed of per-item react-query queries resolve in one network request.
export function loadBookmarkStatus(
  type: BookmarkableType,
  id: number
): Promise<BookmarkLookupItem> {
  return new Promise<BookmarkLookupItem>((resolve, reject) => {
    pendingBookmarkLookups.push({ params: { type, id }, resolve, reject });

    if (!flushScheduled) {
      flushScheduled = true;
      setTimeout(flushBookmarkStatusQueue, 0);
    }
  });
}

export async function createBookmark(values: BookmarkContentParams): Promise<BookmarkResponse> {
  const response = await request('post', api_bookmarks_path(), { bookmark: values });
  return response;
}

export async function destroyBookmark(
  values: BookmarkContentParams
): Promise<BookmarkDestroyResponse> {
  const response = await request('delete', api_bookmarks_path(), { bookmark: values });
  return response;
}

export async function getBookmarks({
  page = 1,
  per_page,
}: BookmarksContentRequest): Promise<BookmarksContentResponse> {
  return request('get', api_bookmarks_path(), { page, per_page });
}
